const PREFIX = process.env.REACT_APP_PREFIX;

export const unAllowedStreetNameCharsRegExp = () => {
    return new RegExp(/[!$%*\\<=>?\[\]^{|}~]/);
};

export const isValidNameRegExp = () => {
    return new RegExp(/[!"#$%&()*+,\\/:;<=>?@\[\]^_`{|}~°ºᵃª]/);
};

export const isValidExtendedLatinRegExp = () => {
    try {
        return new RegExp(
            /^(?!.*[\p{Script=Arabic}\p{Script=Cyrillic}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}]).*$/u
        );
    } catch (e) {
        /**
         * Fallback for older browsers
         * Arabic: \u0600-\u06FF
         * Cyrillic: \u0400-\u04FF\u0500-\u052F
         * Hiragana: \u3040-\u309F
         * Katakana: \u30A0-\u30FF
         * Han (Chinese): \u4E00-\u9FFF
         */
        return new RegExp(/^(?!.*[\u0600-\u06FF\u0400-\u04FF\u0500-\u052F\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]).*$/);
    }
};

export const containNumbersRegExp = () => {
    return new RegExp(/\d/);
};

export const numbersOnlyBeforeAtRegExp = () => {
    return new RegExp(/^[0-9]+@/);
};

export const numbersOnlyAfterAtRegExp = () => {
    return new RegExp(/@([0-9]+)\./);
};

export const isValidEmailRegExp = () => {
    return new RegExp(
        /(?<local>^[a-zA-Z0-9!#$%&'+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'+/=?^_`{|}~-]+)*)@(?<domain>(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+(?<tld>[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$))/
    );
};

/**
 * Postal codes: regex pattern + human-readable example per ISO country code (same keys).
 * Patterns:
 * - https://en.wikipedia.org/wiki/List_of_postal_codes
 * - https://stackoverflow.com/questions/578406/what-is-the-ultimate-postal-code-and-zip-regex
 * - https://gist.github.com/ShreyKumar/43ebb040383c90ed976439245423aa38
 */
const ZIP_CODE_BY_COUNTRY: { [iso: string]: { pattern: RegExp; example: string } } = {
    'AD': { pattern: /^AD\d{3}$/, example: 'AD100' },
    'AI': { pattern: /^AI-2640/, example: 'AI-2640' },
    'AM': { pattern: /^(37)?\d{4}$/, example: '0001' },
    'AR': { pattern: /^([A-HJ-NP-Z])?\d{4}([A-Z]{3})?$/, example: 'C1000ABC' },
    'AS': { pattern: /^96799$/, example: '96799' },
    'AT': { pattern: /^\d{4}$/, example: '1010' },
    'AU': {
        pattern:
            /^(0[289][0-9]{2})|([1345689][0-9]{3})|(2[0-8][0-9]{2})|(290[0-9])|(291[0-4])|(7[0-4][0-9]{2})|(7[8-9][0-9]{2})$/,
        example: '2000',
    },
    'AX': { pattern: /^22\d{3}$/, example: '22100' },
    'AZ': { pattern: /^\d{4}$/, example: '1000' },
    'BA': { pattern: /^\d{5}$/, example: '71000' },
    'BB': { pattern: /^(BB\d{5})?$/, example: 'BB12345' },
    'BD': { pattern: /^\d{4}$/, example: '1200' },
    'BE': { pattern: /^[1-9]{1}[0-9]{3}$/, example: '1000' },
    'BG': { pattern: /^\d{4}$/, example: '1000' },
    'BH': { pattern: /^((1[0-2]|[2-9])\d{2})?$/, example: '100' },
    'BM': { pattern: /^[A-Z]{2}[ ]?[A-Z0-9]{2}$/, example: 'AB 12' },
    'BN': { pattern: /^[A-Z]{2}[ ]?\d{4}$/, example: 'BE3519' },
    'BR': { pattern: /^\d{5}[\-]?\d{3}$/, example: '12345-678' },
    'BY': { pattern: /^\d{6}$/, example: '123456' },
    'CA': {
        pattern: /^([ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ])\ {0,1}(\d[ABCEGHJKLMNPRSTVWXYZ]\d)$/,
        example: 'K1A 0A6',
    },
    'CC': { pattern: /^6799$/, example: '6799' },
    'CH': { pattern: /^\d{4}$/, example: '8000' },
    'CK': { pattern: /^\d{4}$/, example: '1000' },
    'CL': { pattern: /^\d{7}$/, example: '8320000' },
    'CN': { pattern: /^\d{6}$/, example: '100000' },
    'CR': { pattern: /^\d{4,5}|\d{3}-\d{4}$/, example: '10000' },
    'CS': { pattern: /^\d{5}$/, example: '12345' },
    'CV': { pattern: /^\d{4}$/, example: '1000' },
    'CX': { pattern: /^6798$/, example: '6798' },
    'CY': { pattern: /^\d{4}$/, example: '1000' },
    'CZ': { pattern: /^\d{3}[ ]?\d{2}$/, example: '100 00' },
    'DE': {
        pattern: /\b((?:0[1-46-9]\d{3})|(?:[1-357-9]\d{4})|(?:[4][0-24-9]\d{3})|(?:[6][013-9]\d{3}))\b/,
        example: '10115',
    },
    'DK': { pattern: /^([D|d][K|k]( |-))?[1-9]{1}[0-9]{3}$/, example: '2100' },
    'DO': { pattern: /^\d{5}$/, example: '10101' },
    'DZ': { pattern: /^\d{5}$/, example: '16000' },
    'EC': { pattern: /^([A-Z]\d{4}[A-Z]|(?:[A-Z]{2})?\d{6})?$/, example: 'P1234A' },
    'EE': { pattern: /^\d{5}$/, example: '10111' },
    'EG': { pattern: /^\d{5}$/, example: '12345' },
    'ES': { pattern: /^([1-9]{2}|[0-9][1-9]|[1-9][0-9])[0-9]{3}$/, example: '28001' },
    'ET': { pattern: /^\d{4}$/, example: '1000' },
    'FI': { pattern: /^\d{5}$/, example: '00100' },
    'FK': { pattern: /^FIQQ 1ZZ$/, example: 'FIQQ 1ZZ' },
    'FM': { pattern: /^(9694[1-4])([ \-]\d{4})?$/, example: '96941' },
    'FO': { pattern: /^\d{3}$/, example: '100' },
    'FR': { pattern: /^(F-)?((2[A|B])|[0-9]{2})[0-9]{3}$/, example: '75001' },
    'GB': {
        pattern:
            /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})/,
        example: 'SW1A 1AA',
    },
    'GE': { pattern: /^\d{4}$/, example: '0108' },
    'GF': { pattern: /^9[78]3\d{2}$/, example: '97300' },
    'GG': { pattern: /^GY\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/, example: 'GY1 1AA' },
    'GL': { pattern: /^39\d{2}$/, example: '3900' },
    'GN': { pattern: /^\d{3}$/, example: '100' },
    'GP': { pattern: /^9[78][01]\d{2}$/, example: '97100' },
    'GR': { pattern: /^\d{3}[ ]?\d{2}$/, example: '104 31' },
    'GS': { pattern: /^SIQQ 1ZZ$/, example: 'SIQQ 1ZZ' },
    'GT': { pattern: /^\d{5}$/, example: '01001' },
    'GU': { pattern: /^969[123]\d([ \-]\d{4})?$/, example: '96910' },
    'GW': { pattern: /^\d{4}$/, example: '1000' },
    'HM': { pattern: /^\d{4}$/, example: '7151' },
    'HN': { pattern: /^(?:\d{5})?$/, example: '11101' },
    'HR': { pattern: /^\d{5}$/, example: '10000' },
    'HT': { pattern: /^\d{4}$/, example: '6110' },
    'HU': { pattern: /^\d{4}$/, example: '1234' },
    'ID': { pattern: /^\d{5}$/, example: '12345' },
    'IL': { pattern: /^\d{5}(\d{2})?$/, example: '12345' },
    'IM': { pattern: /^IM\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/, example: 'IM1 1AA' },
    'IN': { pattern: /^\d{6}$/, example: '110001' },
    'IO': { pattern: /^BBND 1ZZ$/, example: 'BBND 1ZZ' },
    'IQ': { pattern: /^\d{5}$/, example: '10001' },
    'IS': { pattern: /^\d{3}$/, example: '101' },
    'IT': { pattern: /^(V-|I-)?[0-9]{5}$/, example: '00118' },
    'JE': { pattern: /^JE\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/, example: 'JE1 1AA' },
    'JO': { pattern: /^\d{5}$/, example: '11118' },
    'JP': { pattern: /^\d{3}-\d{4}$/, example: '100-0001' },
    'KE': { pattern: /^\d{5}$/, example: '00100' },
    'KG': { pattern: /^\d{6}$/, example: '123456' },
    'KH': { pattern: /^\d{5}$/, example: '12000' },
    'KR': { pattern: /^\d{3}[\-]?\d{2,3}$/, example: '03130' },
    'KW': { pattern: /^\d{5}$/, example: '12345' },
    'KZ': { pattern: /^\d{6}$/, example: '010000' },
    'LA': { pattern: /^\d{5}$/, example: '01000' },
    'LB': { pattern: /^(\d{4}([ ]?\d{4})?)?$/, example: '1000' },
    'LI': { pattern: /^(948[5-9])|(949[0-7])$/, example: '9490' },
    'LK': { pattern: /^\d{5}$/, example: '10000' },
    'LR': { pattern: /^\d{4}$/, example: '1000' },
    'LS': { pattern: /^\d{3}$/, example: '100' },
    'LT': { pattern: /^\d{5}$/, example: '01101' },
    'LU': { pattern: /^\d{4}$/, example: '1234' },
    'LV': { pattern: /^(LV)?(\s|-)?\d{4}$/, example: 'LV-1001' },
    'MA': { pattern: /^\d{5}$/, example: '10000' },
    'MC': { pattern: /^980\d{2}$/, example: '98000' },
    'MD': { pattern: /^\d{4}$/, example: '2001' },
    'ME': { pattern: /^8\d{4}$/, example: '81000' },
    'MG': { pattern: /^\d{3}$/, example: '101' },
    'MH': { pattern: /^969[67]\d([ \-]\d{4})?$/, example: '96960' },
    'MK': { pattern: /^\d{4}$/, example: '1000' },
    'MN': { pattern: /^\d{6}$/, example: '123456' },
    'MP': { pattern: /^9695[012]([ \-]\d{4})?$/, example: '96950' },
    'MQ': { pattern: /^9[78]2\d{2}$/, example: '97200' },
    'MT': { pattern: /^[A-Z]{3}[ ]?\d{2,4}$/, example: 'VLT 1117' },
    'MU': { pattern: /^\d{5}$/, example: '12345' },
    'MV': { pattern: /^\d{5}$/, example: '12345' },
    'MX': { pattern: /^\d{5}$/, example: '01000' },
    'MY': { pattern: /^\d{5}$/, example: '50000' },
    'NC': { pattern: /^988\d{2}$/, example: '98800' },
    'NE': { pattern: /^\d{4}$/, example: '1000' },
    'NF': { pattern: /^2899$/, example: '2899' },
    'NG': { pattern: /^(\d{6})?$/, example: '100001' },
    'NI': { pattern: /^((\d{4}-)?\d{3}-\d{3}(-\d{1})?)?$/, example: '11001' },
    'NL': { pattern: /^[1-9][0-9]{3}\s?([a-zA-Z]{2})?$/, example: '1011 AB' },
    'NO': { pattern: /^\d{4}$/, example: '0101' },
    'NP': { pattern: /^\d{5}$/, example: '44600' },
    'NZ': { pattern: /^\d{4}$/, example: '6011' },
    'OM': { pattern: /^(PC )?\d{3}$/, example: 'PC 100' },
    'PF': { pattern: /^987\d{2}$/, example: '98700' },
    'PG': { pattern: /^\d{3}$/, example: '100' },
    'PH': { pattern: /^\d{4}$/, example: '1000' },
    'PK': { pattern: /^\d{5}$/, example: '75500' },
    'PL': { pattern: /^\d{2}-\d{3}$/, example: '00-950' },
    'PM': { pattern: /^9[78]5\d{2}$/, example: '97500' },
    'PN': { pattern: /^PCRN 1ZZ$/, example: 'PCRN 1ZZ' },
    'PR': { pattern: /^00[679]\d{2}([ \-]\d{4})?$/, example: '00901' },
    'PT': { pattern: /^\d{4}([\-]\d{3})?$/, example: '1000-001' },
    'PW': { pattern: /^96940$/, example: '96940' },
    'PY': { pattern: /^\d{4}$/, example: '1000' },
    'RE': { pattern: /^9[78]4\d{2}$/, example: '97400' },
    'RO': { pattern: /^\d{6}$/, example: '010101' },
    'RS': { pattern: /^\d{6}$/, example: '11000' },
    'RU': { pattern: /^\d{6}$/, example: '101000' },
    'SA': { pattern: /^\d{5}$/, example: '12345' },
    'SE': { pattern: /^(s-|S-){0,1}[0-9]{3}\s?[0-9]{2}$/, example: '123 45' },
    'SG': { pattern: /^\d{6}$/, example: '123456' },
    'SH': { pattern: /^(ASCN|STHL) 1ZZ$/, example: 'ASCN 1ZZ' },
    'SI': { pattern: /^\d{4}$/, example: '1000' },
    'SJ': { pattern: /^\d{4}$/, example: '9170' },
    'SK': { pattern: /^\d{3}[ ]?\d{2}$/, example: '010 01' },
    'SM': { pattern: /^4789\d$/, example: '47890' },
    'SN': { pattern: /^\d{5}$/, example: '10000' },
    'SO': { pattern: /^\d{5}$/, example: '00101' },
    'SZ': { pattern: /^[HLMS]\d{3}$/, example: 'H100' },
    'TC': { pattern: /^TKCA 1ZZ$/, example: 'TKCA 1ZZ' },
    'TH': { pattern: /^\d{5}$/, example: '10100' },
    'TJ': { pattern: /^\d{6}$/, example: '735700' },
    'TM': { pattern: /^\d{6}$/, example: '744000' },
    'TN': { pattern: /^\d{4}$/, example: '1000' },
    'TR': { pattern: /^\d{5}$/, example: '34000' },
    'TW': { pattern: /^\d{3}(\d{2})?$/, example: '100' },
    'UA': { pattern: /^\d{5}$/, example: '01001' },
    'US': { pattern: /^\d{5}([ \-]\d{4})?$/, example: '12345-6789' },
    'UY': { pattern: /^\d{5}$/, example: '11000' },
    'UZ': { pattern: /^\d{6}$/, example: '100000' },
    'VA': { pattern: /^00120$/, example: '00120' },
    'VE': { pattern: /^\d{4}$/, example: '1000' },
    'VI': { pattern: /^008(([0-4]\d)|(5[01]))([ \-]\d{4})?$/, example: '00802' },
    'WF': { pattern: /^986\d{2}$/, example: '98600' },
    'XK': { pattern: /^\d{5}$/, example: '10000' },
    'YT': { pattern: /^976\d{2}$/, example: '97600' },
    'YU': { pattern: /^\d{5}$/, example: '11000' },
    'ZA': { pattern: /^\d{4}$/, example: '8001' },
    'ZM': { pattern: /^\d{5}$/, example: '10101' },
};

export const isValidZipCodeRegExp = (countryCode: string | null) => {
    return new RegExp(ZIP_CODE_BY_COUNTRY[countryCode || '']?.pattern);
};

export const getPostalCodeFormatExample = (countryCode: string | null | undefined): string | undefined => {
    if (countryCode == null || countryCode === '') {
        return undefined;
    }
    return ZIP_CODE_BY_COUNTRY[countryCode.toUpperCase()]?.example;
};

export const generateSrcRegExp = (key: string) => {
    return new RegExp(`(?:${key})\\=\\d+`, 'g');
};

export const generateSrcSetRegExp = (heights: number[]) => {
    return new RegExp(`(\\?|\\&){0,1}(?:${[heights].length ? 'w|h' : 'w'})\\=\\d+\\&{0,1}`, 'g');
};

export const isFileRegExp = () => {
    return new RegExp(/.+\.[a-zA-Z0-9]+$/);
};

export const removePathPrefixRegExp = () => {
    return new RegExp(`^/${PREFIX}`);
};
